import { showToast, Typography, Tables } from '@deeptrue-frontend/ui';
import React from 'react';
import RemoveModal from 'components/PromptModal/PromptModal';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import Tooltip from '@material-ui/core/Tooltip';
import styled from 'styled-components/macro';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import api from 'api';
import { InitialValues } from '../GenererateLinkForm/GenerateLinkForm';
import { useFormatMessage } from 'components/IntlProvider';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';

const { TableRow, TableCell } = Tables;

const IconsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  svg {
    margin: 0 0.25rem;
    cursor: pointer;
  }
`;
const Title = styled(Typography)`
  max-width: 30rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

export type LinkType = {
  id: number;
  access_type: 'public' | 'password_protected';
  default_language: 'en' | 'fr' | 'de' | 'it';
  title: string;
  description: string;
  is_disabled: boolean;
  url: string;
  uuid: string;
  sections: string[];
  password?: string;
};

interface Props {
  linkDetails: LinkType;
  organization: any;
  surveyId: string;
  index: number;
  removeElement: (idx: number) => void;
  setInitialValues: (values: InitialValues) => void;
  setIsFormOpen: (val: boolean) => void;
  updateLink: (link: LinkType) => void;
}

export const Link = ({
  linkDetails,
  organization,
  surveyId,
  removeElement,
  index,
  setInitialValues,
  setIsFormOpen,
  updateLink,
}: Props) => {
  const t = useFormatMessage();

  const removeLink = (id: number) => {
    return api.resources
      .deleteSharingLink({
        organizationCodename: organization,
        surveyId,
        linkId: id,
      })
      .then(() => {
        removeElement(index);
      });
  };

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(linkDetails.url);

    showToast({
      type: 'success',
      title: t(
        'project.share.shareReportLinks.linkCopied',
        'Link copied to clipboard'
      ),
    });
  };

  const updateLinkActiveState = (linkDetails: LinkType, newStatus: boolean) => {
    return api.resources
      .toggleStatusSharingLink({
        organizationCodename: organization,
        linkId: linkDetails.id,
        surveyId: surveyId,
        isDisabled: newStatus,
      })
      .then((resp) => {
        updateLink(resp.data);
        showToast({
          type: 'success',
          title: t('project.share.shareReportLinks.linkEdited', 'Link edited'),
        });
        setIsFormOpen(false);
      });
  };

  return (
    <TableRow>
      <TableCell minWidth="150px" height="2rem" padding="0.5rem">
        <Title>{linkDetails.title}</Title>
      </TableCell>
      <TableCell height="2rem" padding="0.5rem">
        {linkDetails.is_disabled}
        <span style={{ color: !linkDetails.is_disabled ? 'black' : 'red' }}>
          {!linkDetails.is_disabled
            ? t('organization.active', 'Active')
            : t('organization.deactive', 'Deactivated')}
        </span>
      </TableCell>
      <TableCell height="2rem" padding="0.5rem" align="right">
        <IconsWrapper>
          <Tooltip title={t('project.share.shareReportLinks.copy', 'Copy')}>
            <FileCopyIcon onClick={copyLinkToClipboard} />
          </Tooltip>
          <Tooltip title={t('project.share.shareReportLinks.edit', 'Edit')}>
            <EditIcon
              onClick={() => {
                setInitialValues({
                  access_type: linkDetails.access_type,
                  default_language: linkDetails.default_language,
                  title: linkDetails.title,
                  description: linkDetails.description,
                  sections: linkDetails.sections,
                  id: linkDetails.id,
                  ...(linkDetails.password
                    ? { password: linkDetails.password }
                    : {}),
                  is_disabled: linkDetails.is_disabled,
                });
                setIsFormOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title={t('project.share.shareReportLinks.delete', 'Delete')}>
            <span style={{ cursor: 'pointer' }}>
              <RemoveModal
                onConfirm={() => removeLink(linkDetails.id)}
                renderer={({ handleOpen }: { handleOpen: any }) => (
                  <DeleteIcon onClick={handleOpen} />
                )}
              >
                <>
                  {t(
                    'project.share.shareReportLinks.deleteConfirm',
                    'Are you sure you want to delete this link?'
                  )}
                </>
              </RemoveModal>
            </span>
          </Tooltip>
          <Tooltip
            title={t(
              'project.share.shareReportLinks.activeDeactive',
              'Active/Deactive'
            )}
          >
            <span style={{ cursor: 'pointer' }}>
              <RemoveModal
                onConfirm={() =>
                  updateLinkActiveState(linkDetails, !linkDetails.is_disabled)
                }
                renderer={({ handleOpen }: { handleOpen: any }) => (
                  <PowerSettingsNewIcon onClick={handleOpen} />
                )}
                isPositive={linkDetails.is_disabled}
              >
                <>
                  {t(
                    'project.share.shareReportLinks.activeDeactive.confirm',
                    'Are you sure you want to {action} link?',
                    {
                      action: linkDetails.is_disabled
                        ? t(
                            'project.share.shareReportLinks.activeDeactive.activate',
                            'activate'
                          )
                        : t(
                            'project.share.shareReportLinks.activeDeactive.deactivate',
                            'deactivate'
                          ),
                    }
                  )}
                </>
              </RemoveModal>
            </span>
          </Tooltip>
        </IconsWrapper>
      </TableCell>
    </TableRow>
  );
};
